import { formatISO, startOfDay, subDays } from "date-fns";
import { NotificationsPopover } from "src/features/notifications/shared/components/organisms/notifications-popover";
import { useMarkSingleNotificationAsRead } from "../../hooks/use-mark-single-notification-as-read";
import { useMarkSingleNotificationAsUnread } from "../../hooks/use-mark-single-notification-as-unread";
import { useMarkAllNotificationsAsRead } from "../../hooks/use-mark-all-notifications-as-read";
import { useSettings } from "src/contexts/settings-context";
import { useInfiniteNotifications } from "../../hooks/use-infinite-notifications";
import { useCallback, useEffect, useState } from "react";
import { NotificationSearchState } from "src/features/notifications/shared/types/payloads/notification-search-state.payload";
import { useQueryClient } from "@tanstack/react-query";
import { notificationsKeys } from "../../api/lib/query-keys/notifications";
import { siteNotificationsApi } from "../../api/lib/notifications";
import { useUnreadNotificationsCountStore } from "src/features/notifications/shared/store/unread-notifications-count-store";
import useStore from "src/hooks/use-store";

const PER_PAGE = 10;

export default function TenantNotificationsPopover() {
  const { settings } = useSettings();

  const { user } = useStore();

  const queryClient = useQueryClient();

  const [searchState, setSearchState] = useState<NotificationSearchState>({
    lang: settings.language,
    dateFrom: formatISO(startOfDay(subDays(new Date(), 30))),
    dateTo: formatISO(new Date()),
  });

  const unreadNotificationsCount = useUnreadNotificationsCountStore(
    state => state.unreadNotificationsCount,
  );
  const setUnreadNotificationsCount = useUnreadNotificationsCountStore(
    state => state.setUnreadNotificationsCount,
  );

  const decrementUnreadNotificationsCount = useUnreadNotificationsCountStore(
    state => state.decrementUnreadNotificationsCount,
  );

  const incrementUnreadNotificationsCount = useUnreadNotificationsCountStore(
    state => state.incrementUnreadNotificationsCount,
  );

  const { data, fetchNextPage, hasNextPage, isFetchingNextPage } = useInfiniteNotifications(
    PER_PAGE,
    searchState,
  );

  const notifications = data?.pages?.flatMap(page => page.items);

  const prefetchTabData = useCallback(
    async (searchState: NotificationSearchState) => {
      if (searchState?.read === false) {
        const { read, ...rest } = searchState;

        await queryClient.prefetchInfiniteQuery({
          queryKey: notificationsKeys.infiniteList(PER_PAGE, { ...rest }),
          queryFn: () =>
            siteNotificationsApi.fetchNotifications(1, PER_PAGE, {
              ...searchState,
              read: false,
            }),
        });
      } else {
        await queryClient.prefetchInfiniteQuery({
          queryKey: notificationsKeys.infiniteList(PER_PAGE, { ...searchState, read: false }),
          queryFn: () =>
            siteNotificationsApi.fetchNotifications(1, PER_PAGE, {
              ...searchState,
              read: false,
            }),
        });
      }
    },
    [queryClient],
  );

  useEffect(() => {
    //once at a start + after context changes + after window focus
    const prefetchUnreadNotificationsAndUpdateCount = () => {
      prefetchTabData(searchState).then(_ => {
        const unreadCount = queryClient?.getQueryData(
          notificationsKeys.infiniteList(PER_PAGE, { ...searchState, read: false }),
          //@ts-ignore
        )?.pages?.[0]?.pagination?.total;

        setUnreadNotificationsCount(unreadCount ?? 0);
      });
    };

    prefetchUnreadNotificationsAndUpdateCount();

    const handleTabVisible = () => {
      if (document.visibilityState === "visible") {
        prefetchUnreadNotificationsAndUpdateCount();
      }
    };

    document.addEventListener("visibilitychange", handleTabVisible);

    return () => {
      document.removeEventListener("visibilitychange", handleTabVisible);
    };
  }, [user.siteToken, user.viewContext, user.userOrganizationId]);

  const { mutateAsync: markNotificationAsRead, isPending: isMarkingNotificationAsRead } =
    useMarkSingleNotificationAsRead(data => {
      decrementUnreadNotificationsCount(data.updatedCount);
      prefetchTabData(searchState);
    });
  const { mutateAsync: markNotificationsAsRead, isPending: isMarkingNotificationsAsRead } =
    useMarkAllNotificationsAsRead(data => {
      decrementUnreadNotificationsCount(data.updatedCount);
      prefetchTabData(searchState);
    });
  const { mutateAsync: markNotificationAsUnread, isPending: isMarkingNotificationAsUnread } =
    useMarkSingleNotificationAsUnread(data => {
      incrementUnreadNotificationsCount(data.updatedCount);
      prefetchTabData(searchState);
    });

  const isChangingReadStatusDisabled =
    isMarkingNotificationAsRead || isMarkingNotificationsAsRead || isMarkingNotificationAsUnread;

  useEffect(() => {
    setSearchState(prev => ({ ...prev, lang: settings.language }));
  }, [settings.language]);

  return (
    <NotificationsPopover
      notifications={notifications}
      unreadNotificationsCount={unreadNotificationsCount}
      isLoading={isFetchingNextPage}
      hasNextPage={hasNextPage}
      fetchNextPage={fetchNextPage}
      setSearchState={setSearchState}
      onNotificationMarkAsRead={markNotificationAsRead}
      onNotificationMarkAsUnread={markNotificationAsUnread}
      onNotificationsMarkAsRead={markNotificationsAsRead}
      isChangingReadStatusDisabled={isChangingReadStatusDisabled}
    />
  );
}
